<template>
    <div>
        <simple-dropdown
            v-if="useDropdown"
            position="right"
        >
            <template #trigger="{ isOpen }">
                <tooltip
                    ref="popper"
                    :force-show="allowPopper && !isOpen && hasUnreadNotifications"
                    position="bottom"
                    tooltip-class="cursor-pointer"
                    @tooltip-click="clearTooltip"
                >
                    <button
                        class="w-32 h-32 flex items-center justify-center cursor-pointer text-primary hover:text-secondary disabled:cursor-default disabled:text-primary-40"
                        :disabled="!notifications.length"
                        @click="clearTooltip()"
                    >
                        <svg-icon :size="iconSize" :type="icon"></svg-icon>
                    </button>
                    <template #tooltip>
                        {{ $tc('notifications.unread_count', unreadNotificationCount, {count: unreadNotificationCount}) }}
                    </template>
                </tooltip>
            </template>
            <template #content="{ close }">
                <div class="flex flex-col items-center">
                    <div class="border-b border-primary-20 w-full">
                        <div class="p-16 text-black inline-flex items-center justify-between w-full">
                            <div class="flex items-center">
                                <svg-icon type="notifications" />
                                <p class="font-semibold text-body first-letter:uppercase ml-4">{{ $t('notifications.title') }}</p>
                            </div>
                            <simple-button
                                :disabled="working || !hasUnreadNotifications"
                                :loading="working"
                                class="text-nowrap ml-108"
                                small
                                @click="markAllRead"
                            >
                                {{ $t('notifications.mark_all_read') }}
                            </simple-button>
                        </div>
                    </div>
                    <div class="max-h-320 overflow-y-auto w-full">
                        <ul>
                            <alert
                                v-for="notification in notifications"
                                :key="'notification-' + notification.id"
                                :application-name="applicationName(notification)" :job-name="jobName(notification)"
                                :message="message(notification)" :notification="notification" :user-avatar="userAvatar(notification)" :user-name="userName(notification)"
                                @click="visitNotificationSource(notification); close()"
                                :is-last-item="notifications.indexOf(notification) === notifications.length - 1"
                            ></alert>
                        </ul>
                        <infinite-loading :slots="{complete: ' ', error: ' '}"
                                          @infinite="infiniteHandler">
                            <template #spinner>
                                <div class="p-16">
                                    <loading size="small"></loading>
                                </div>
                            </template>
                        </infinite-loading>
                    </div>
                </div>
            </template>
        </simple-dropdown>


        <template v-if="!useDropdown">
            <div>
                <simple-button
                    class="w-full mt-12"
                    :disabled="working || !hasUnreadNotifications"
                    :loading="working"
                    small
                    @click="markAllRead"
                >
                    {{ $t('notifications.mark_all_read') }}
                </simple-button>
                <div class="max-h-564 min-h-480 overflow-y-auto pt-12">
                    <ul>
                        <alert
                            v-for="notification in notifications.filter(x => canManage(x))"
                            :key="'notification-' + notification.id"
                            :application-name="applicationName(notification)"
                            :job-name="jobName(notification)"
                            :message="message(notification)"
                            :notification="notification"
                            :user-avatar="userAvatar(notification)"
                            :user-name="userName(notification)"
                            @click="visitNotificationSource(notification);"
                        ></alert>
                    </ul>
                    <infinite-loading :slots="{complete: ' ', error: ' '}"
                                      @infinite="infiniteHandler">
                        <template #spinner>
                            <div class="p-16">
                                <loading size="small"></loading>
                            </div>
                        </template>
                    </infinite-loading>
                </div>
            </div>
        </template>
        <template v-else>
            <div v-if="hasUnreadNotifications" class="pointer-events-none absolute top-0 right-0 w-12 h-12">
                <div class="bg-semantic-warning w-full h-full rounded-32"></div>
                <div class="pulsating absolute inset-0 bg-semantic-warning w-full h-full rounded-32"></div>
            </div>
        </template>
    </div>
</template>

<script>
import SvgIcon from './SvgIcon.vue';
import * as notifications from '../../services/NotificationService';
import Notification from '../../models/Notification';
import {normalizeJson} from '../../../shared/helpers';
import SimpleDropdown from './SimpleDropdown.vue';
import InfiniteLoading from 'v3-infinite-loading/lib/v3-infinite-loading.es.js';
import Loading from './Loading.vue';
import Alert from './Alert.vue';
import Tooltip from '../navigation/Tooltip';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import notificationMixin from '../../mixins/notifications';
import SimpleButton from "./SimpleButton.vue";
import {useMainStore} from "../../state/store";
import {mapState, mapStores} from "pinia";
import {useRepo} from "pinia-orm";
import bus from "../../../shared/eventbus";
import {useNotificationsStore} from "../../state/modules/notifications";

export default {
    mixins: [notificationMixin],
    components: {
        SimpleButton,
        Tooltip,
        'svg-icon': SvgIcon,
        'simple-dropdown': SimpleDropdown,
        'infinite-loading': InfiniteLoading,
        'loading': Loading,
        'alert': Alert,
    },
    props: {
        useDropdown: {type: Boolean, default: true},
        useTitle: {type: Boolean, default: false},
        fetch: Boolean,
        icon: String,
        iconSize: {type: String, default: '16'},
        tooltip: String,
    },
    data: function () {
        return {
            allowPopper: null,
            title: 'Jobtoolz',
            working: false,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        ...mapState(useMainStore, ['locale']),
        currentUserId() {
            return this.mainStore.currentUser?.id;
        },
        currentCompanyId() {
            return this.mainStore.currentCompany?.id;
        },
        notifications() {
            return useRepo(Notification)
                .where('notifiable_type', 'users')
                .where('notifiable_id', this.currentUserId)
                .where('company_id', this.currentCompanyId)
                .orderBy('created_at', 'desc')
                .get();
        },
        notificationCount() {
            return this.notifications.length;
        },
        unreadNotificationCount() {
            return this.notifications.filter(notification => !notification.read_at).length;
        },
        hasUnreadNotifications() {
            this.mainStore.setNotificationCount(this.unreadNotificationCount);

            return this.unreadNotificationCount !== 0;
        },
        documentTitle() {
            return (
                this.hasUnreadNotifications
                    ? '(' + this.unreadNotificationCount + ')'
                    : ''
            ) + ' ' + (
                this.mainStore.documentTitle !== null
                    ? this.mainStore.documentTitle + ' | '
                    : ''
            ) + ' Jobtoolz';
        },
    },
    head() {
        return {
            title: this.documentTitle,
        };
    },
    methods: {
        infiniteHandler(state) {
            return this.fetchNotifications(this.notificationCount)
                .then(inserts => {
                    if (inserts.notifications && inserts.notifications.length) {
                        state.loaded();
                    } else {
                        state.complete();
                    }
                });
        },
        fetchNotifications(offset) {

            let limit = 10;

            return notifications.index(this.currentUserId, this.currentCompanyId, {offset, limit})
                .then(result => {
                    return useRepo(Notification).save(normalizeJson(result.data));
                })
                .catch(error => {
                    console.error('error', error)
                    if (error.response.status && error.response.status === 401) {
                        // Most likely the session timed out, let's head back to login with a reference
                        this.$router.push({query: Object.assign({}, this.$router.currentRoute.value.query, {reason: 'timeout', lang: this.locale})});
                        location.reload();
                    }
                });
        },
        markAllRead() {
            this.working = true;

            notifications.markAllRead(this.currentUserId)
                .then(response => {
                    useRepo(Notification)
                        .where('notifiable_id', this.currentUserId)
                        .update({read_at: response.data.read_at});

                    this.working = false;
                });
        },
        clearTooltip() {
            this.allowPopper = false;
        },
        message(notification) {
            switch (notification.type.split('\\').slice(-1)[0]) {
                case 'BulkActionNotification':
                    return notification.data.message
                case 'CommentNotification':
                    return this.$t('interim.comment_shared');
                case 'IntroduceCreated':
                    return this.$t('general.new_subject', {subject: this.$t('applications.applicant')});
                case 'ApplicationCreated':
                    return this.$t('notifications.application.new', {application: this.applicationName(notification)});

                case 'CandidateIntroduced':
                    return this.$t('general.new_subject', {subject: this.$t('applications.application')});

                case 'ApplicationUpdated':
                    return this.$t('notifications.application.updated', {application: this.applicationName(notification)});

                case 'ApplicationStageMoved':
                    return this.$t('notifications.application.stage_moved', {
                        // 'user': this.userName(notification),
                        'application': this.applicationName(notification),
                        'stage': notification.data.stage.title ? notification.data.stage.title[this.locale] || '' : '',
                    });

                case 'ApplicationJobMoved':
                    return this.$t('notifications.application.job_moved');

                case 'ApplicationScoreUpdated':
                    return this.$t('notifications.application.score_updated', {'application': this.applicationName(notification)});

                case 'ApplicationRejected':
                    return this.$t('notifications.application.rejected', {'user': this.userName(notification)});

                case 'ApplicationUnrejected':
                    return this.$t('notifications.applications.unrejected', {'user': this.userName(notification)});

                case 'DocumentCreated':
                    return this.$t('notifications.document_created', {application: this.applicationName(notification)});

                case 'DocumentDeleted':
                    return this.$t('notifications.document_deleted', {application: this.applicationName(notification)});

                case 'InterviewCreated':
                    return this.$t('notifications.interview_created', {application: this.applicationName(notification)});

                case 'InterviewUpdated':
                    return this.$t('notifications.interview_updated', {application: this.applicationName(notification)});

                case 'InterviewConfirmed':
                    return this.$t('notifications.application.interviewConfirmed', {application: this.applicationName(notification)});

                case 'InterviewRescheduledByInterviewee':
                    return this.$t('notifications.interview_rescheduled_by_interviewee', {application: this.applicationName(notification)});

                case 'InterviewCanceledByInterviewee':
                    return this.$t('notifications.interview_cancelled_by_interviewee', {application: this.applicationName(notification)});

                case 'InterviewDeleted':
                    return this.$t('notifications.interview_deleted', {application: this.applicationName(notification)});

                case 'ContactCreated':
                    return this.$t('timeline.contact_created');

                case 'InboundEmailCreated':
                    return this.$t('notifications.inbound_created', {'sender': this.applicationName(notification)});

                case 'InvitationCreated':
                    return this.$t('timeline.invitation_created');

                case 'ApplicationCommentCreated':
                    return this.$t('notifications.comment_created', {'application': this.applicationName(notification)});

                case 'UserMentioned':
                    return this.$t('notifications.user_mentioned', {
                        // 'user': this.userName(notification),
                        'application': this.applicationName(notification),
                    });

                case 'JobCreated':
                    return this.$t('notifications.job_created', {
                        'user': this.userName(notification),
                        'job': this.jobName(notification),
                    });

                case 'InterviewInvitationConfirmed':
                    return this.$t('notifications.application.interviewConfirmed', {
                        application: this.applicationName(notification),
                    });
                case 'TaskAssigned':
                    return this.$t('notifications.task_assigned', {
                        actor_name: notification.data?.updated_by?.name || this.$t('general.someone'),
                        title: notification.data.task.title,
                    });

                case 'TaskDeadlineReached':
                    return notification.data.task.title;

                case 'MailFailedToSend':
                    return this.$t('notifications.mail_failed_to_send', {
                        user: this.applicationName(notification),
                    });

                case 'ImportDataFinished':
                    return this.$t('mail.notifications.import_finished.' + (notification.data.success ? 'success' : 'fail') + '.subject', {
                        company: notification.data.company.name,
                    });

                case 'ExportDataFinished':
                    return this.$t('analytics.exports.notification.message');

                default:
                    return this.$t('notifications.unknown');
            }
        },
        userName(notification) {
            if (!notification.data.user || !notification.data.user.name) {
                return;
                // return this.$t('general.someone');
            }

            return notification.data.user.name;
        },
        userAvatar(notification) {
            if (!notification.data.user || !notification.data.user.avatar) {
                return;
            }

            return notification.data.user.avatar;
        },
        applicationName(notification) {
            if (!notification.data.application) {
                return;
            }

            return [notification.data.application.first_name || '', notification.data.application.last_name || '']
                .filter(val => val)
                .join(' ');
        },
        jobName(notification) {
            if (!notification.data.job || !notification.data.job.title) {
                return;
            }
            return this.translated(notification.data.job.title, this.locale, true);
        },
        canManage(notification){
            if (!notification || (!notification.data.application && !notification.data.job)) {
                return true;
            }

            return !!this.$can([
                'applications.view',
                'applications.view.' + notification.data.application?.id,
                'job-applications.view.' + notification.data.job?.id,
            ]);
        }
    },
    mounted() {
        bus.addEventListener('switched-company', () => {
            this.fetchNotifications(0);
        });

        if (this.fetch) {
            this.fetchNotifications(0);
        }

        setTimeout(() => {
            if (this.allowPopper === null) {
                this.allowPopper = true;
            }
        }, 4000);

        Echo.private('notifications.' + this.currentUserId)
            .listen('NotificationCreated', async ({notification}) => {
                let n = useRepo(Notification).save(normalizeJson(notification));

                const notificationsStore = useNotificationsStore();

                notificationsStore[ADD_NOTIFICATION]({
                    type: 'neutral',
                    message: this.message(normalizeJson(notification)),
                    notification: n,
                });
            });
    },
    beforeUnmount() {
        if (this.$interval) {
            clearInterval(this.$interval);
        }
    },
};
</script>

<style scoped>
.pulsating {
    transform-origin: center;
    animation: pulsate 2s infinite;
}

@keyframes pulsate {
    0% {
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: scale(3);
    }
}
</style>
